import { useState } from "react";
import { api } from "@/lib/apiClient";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Terminal } from "@phosphor-icons/react";
import { toast } from "sonner";
import { InstallerContent } from "@/components/InstallerDialog";

export default function AddMachineDialog({ open, onOpenChange, onCreated, orgId }) {
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);

  const submit = async () => {
    if (!name.trim() || !orgId) return;
    setCreating(true);
    try {
      const res = await api.post(`/orgs/${orgId}/machines`, { name: name.trim() });
      setCreated(res.data);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create machine");
    } finally {
      setCreating(false);
    }
  };

  const reset = () => {
    setName("");
    setCreated(null);
    onOpenChange(false);
  };

  const refreshInstallLink = async (m) => {
    const res = await api.post(`/orgs/${orgId}/machines/${m.machine_id}/regenerate-token`);
    setCreated(res.data);
    onCreated();
    toast.success("Fresh install link generated. Valid for 15 minutes.");
    return res.data;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : reset())}>
      <DialogContent className="bg-zinc-900 border-white/10 sm:max-w-lg max-h-[88vh] overflow-y-auto" data-testid="add-machine-dialog">
        {!created ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl tracking-tight">Add a machine</DialogTitle>
              <DialogDescription className="text-zinc-400 text-sm">
                Give this computer a name. You'll get a pairing token and a command to run on it.
              </DialogDescription>
            </DialogHeader>
            <div className="py-2">
              <Label className="text-sm text-zinc-300">Machine name</Label>
              <Input
                data-testid="machine-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                placeholder="e.g. Office Desktop"
                className="mt-2 bg-black border-zinc-800 focus-visible:ring-blue-600"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={reset} className="text-zinc-400" data-testid="cancel-add-machine">Cancel</Button>
              <Button
                data-testid="create-machine-button"
                onClick={submit}
                disabled={creating || !name.trim()}
                className="bg-blue-600 hover:bg-blue-500"
              >
                {creating ? "Creating…" : "Create machine"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl tracking-tight flex items-center gap-2">
                <Terminal size={20} weight="bold" className="text-blue-500" /> Install the agent
              </DialogTitle>
              <DialogDescription className="text-zinc-400 text-sm">
                Download the installer, then run it on <span className="text-zinc-200 font-medium">{created.name}</span>. You can reopen this anytime from the machine's menu → "Get installer".
              </DialogDescription>
            </DialogHeader>
            <InstallerContent machine={created} onRefresh={refreshInstallLink} />
            <DialogFooter>
              <Button data-testid="done-add-machine" onClick={reset} className="bg-blue-600 hover:bg-blue-500">Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
